import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, CheckCircle2, ChevronDown, ChevronRight, FileText, Globe2, Home,
  ListTree, Loader2, Lock, MessageCircle, Package, Plane, RotateCcw, ShieldCheck,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";

export const LEGAL_NAV = [
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "refund", label: "Refund Policy" },
  { slug: "shipping", label: "Shipping Policy" },
  { slug: "prohibited", label: "Prohibited Items" },
];

const PAGE_META = {
  terms: { seo: "Terms of Service | IndiaFast Global Couriers", desc: "The terms that govern your use of IndiaFast Global Couriers services and website.", Icon: FileText, cross: [["Privacy Policy", "/p/privacy"], ["Refund Policy", "/p/refund"], ["Shipping Policy", "/p/shipping"], ["Prohibited Items", "/p/prohibited"]] },
  privacy: { seo: "Privacy Policy | IndiaFast Global Couriers", desc: "How IndiaFast collects, uses, protects and manages information.", Icon: Lock, cross: [["Terms of Service", "/p/terms"], ["Contact IndiaFast", "/support"]] },
  refund: { seo: "Refund Policy | IndiaFast Global Couriers", desc: "Clear information about cancellations, refunds, credits and payment adjustments.", Icon: RotateCcw, cross: [["Terms of Service", "/p/terms"], ["Shipping Policy", "/p/shipping"], ["Contact IndiaFast", "/support"]] },
  shipping: { seo: "Shipping Policy | IndiaFast Global Couriers", desc: "Everything you need to know before sending your shipment with IndiaFast.", Icon: Plane, cross: [["Request a Pickup", "/pickup-request"], ["Track Shipment", "/track"], ["Prohibited Items", "/p/prohibited"], ["NRI Courier", "/nri-courier"], ["Terms of Service", "/p/terms"]] },
};

export const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export function LegalNav({ current }) {
  return (
    <div className="sticky top-16 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl" data-testid="legal-nav">
      <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 sm:px-6">
        {LEGAL_NAV.map((l) => (
          <Link key={l.slug} to={`/p/${l.slug}`} data-testid={`legal-nav-${l.slug}`}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${current === l.slug ? "bg-navy text-saffron shadow-lg" : "bg-slate-100 text-slate-600 hover:bg-saffron/10 hover:text-saffron"}`}>
            {l.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function NeedHelp({ contact }) {
  const waNum = (contact?.phone || "").replace(/\D/g, "").slice(-10);
  return (
    <section className="bg-navy py-14" data-testid="legal-need-help">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Need Help?</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-slate-300">Not sure about a shipment, payment, refund or restricted item?</p>
        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/support" data-testid="legal-help-contact" className="rounded-full bg-saffron px-7 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Contact IndiaFast</Link>
          {waNum && <a href={`https://wa.me/91${waNum}`} target="_blank" rel="noreferrer" data-testid="legal-help-whatsapp"
                       className="flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-3 text-sm font-bold text-white hover:bg-emerald-500"><MessageCircle className="h-4 w-4" /> WhatsApp Support</a>}
          <Link to="/rate-calculator" data-testid="legal-help-quote" className="rounded-full border border-white/25 px-7 py-3 text-sm font-bold text-white hover:bg-white/10">Get a Quote</Link>
        </div>
      </div>
    </section>
  );
}

function HeroIcons({ slug }) {
  const iconCls = "h-7 w-7";
  const sets = {
    terms: [FileText, Package, CheckCircle2],
    privacy: [ShieldCheck, Lock, FileText],
    refund: [RotateCcw, Package, CheckCircle2],
    shipping: [Globe2, Plane, Package],
  };
  const Icons = sets[slug] || sets.terms;
  return (
    <div className="relative hidden h-40 items-center justify-center gap-4 lg:flex" aria-hidden="true">
      {Icons.map((Icon, i) => (
        <span key={i} className={`grid place-items-center rounded-2xl border border-white/10 bg-white/5 text-saffron backdrop-blur ${i === 1 ? "h-24 w-24" : "h-16 w-16"}`}>
          <Icon className={iconCls} />
        </span>
      ))}
      <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-30">
        <line x1="15%" y1="70%" x2="50%" y2="45%" stroke="#f97316" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
        <line x1="50%" y1="45%" x2="85%" y2="65%" stroke="#16a34a" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
      </svg>
    </div>
  );
}

function RefundTimeline() {
  const steps = ["Request", "Review", "Approval", "Processing", "Customer Account"];
  return (
    <div className="my-6 rounded-2xl bg-slate-50 p-5" data-testid="refund-timeline">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">How refunds move</p>
      <div className="mt-3 flex items-center gap-1 overflow-x-auto">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center">
            <span className="grid shrink-0 place-items-center gap-1 rounded-xl border border-saffron/30 bg-white px-3 py-2 text-center">
              <RotateCcw className="mx-auto h-3.5 w-3.5 text-saffron" />
              <span className="text-[10px] font-bold text-navy">{s}</span>
            </span>
            {i < steps.length - 1 && <ChevronRight className="h-4 w-4 shrink-0 text-saffron" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackingTimeline() {
  const steps = ["Booked", "Picked Up", "Processing", "In Transit", "Destination", "Out for Delivery", "Delivered"];
  return (
    <div className="my-6 rounded-2xl bg-navy p-5" data-testid="shipping-tracking-visual">
      <div className="flex items-center gap-0.5 overflow-x-auto">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center">
            <span className={`shrink-0 rounded-full px-2.5 py-1.5 text-[9px] font-bold ${i === steps.length - 1 ? "bg-emerald-600 text-white" : "bg-white/10 text-slate-300"}`}>{s}</span>
            {i < steps.length - 1 && <span className="mx-0.5 h-px w-3 shrink-0 bg-saffron/60" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function PackingVisual() {
  const layers = [["Product", "bg-saffron"], ["Inner Protection", "bg-express"], ["Waterproof Layer", "bg-navy"], ["Outer Packaging", "bg-emerald-600"], ["Shipping Label", "bg-slate-500"], ["Courier Pickup", "bg-saffron"]];
  return (
    <div className="my-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="shipping-packing-visual">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pack it right — layer by layer</p>
      <div className="mt-3 space-y-1.5">
        {layers.map(([l, c], i) => (
          <div key={l} className={`mx-auto rounded-lg px-3 py-2 text-center text-[10px] font-bold text-white ${c}`} style={{ width: `${45 + i * 11}%`, minWidth: "180px" }}>{l}</div>
        ))}
      </div>
    </div>
  );
}

function PrivacyFlow() {
  return (
    <div className="my-6 flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-slate-50 p-5" data-testid="privacy-flow">
      {["Your Data", "Secure Processing", "Protected Service"].map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-full border border-emerald-600/30 bg-white px-4 py-2 text-xs font-bold text-navy">
            {i === 1 ? <Lock className="h-3.5 w-3.5 text-emerald-600" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}{s}
          </span>
          {i < 2 && <ArrowRight className="h-4 w-4 text-saffron" />}
        </div>
      ))}
    </div>
  );
}

export default function LegalCenter({ slug }) {
  const [page, setPage] = useState(null);
  const [contact, setContact] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  const meta = PAGE_META[slug];

  usePageMeta(meta.seo, meta.desc, `/p/${slug}`);
  useEffect(() => {
    setPage(null);
    api.get(`/legal/public/${slug}`).then((r) => setPage(r.data)).catch(() => {});
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) return <div className="grid min-h-screen place-items-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`legal-${slug}-page`}>
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-16 pt-32 sm:pt-36" data-testid="legal-hero">
        {page.hero_image_path && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${page.hero_image_path}`} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/85 to-navy" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-6 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <nav className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400" data-testid="legal-breadcrumb">
              <Link to="/" className="flex items-center gap-1 hover:text-saffron"><Home className="h-3 w-3" /> Home</Link>
              <ChevronRight className="h-3 w-3" /> <span>Legal</span>
              <ChevronRight className="h-3 w-3" /> <span className="text-saffron">{page.title}</span>
            </nav>
            <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="legal-title">{page.title}</h1>
            {page.subtitle && <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">{page.subtitle}</p>}
            <div className="mt-5 flex flex-wrap gap-2" data-testid="legal-docinfo">
              {page.last_updated && <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-bold text-slate-300">Last Updated: {page.last_updated}</span>}
              {page.effective_from && <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-bold text-slate-300">Effective From: {page.effective_from}</span>}
              {page.version && <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-bold text-slate-300">Version {page.version}</span>}
            </div>
          </div>
          <HeroIcons slug={slug} />
        </div>
      </section>

      <LegalNav current={slug} />

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:grid lg:grid-cols-4 lg:gap-10">
        {/* TOC */}
        <aside className="mb-8 lg:mb-0">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-32" data-testid="legal-toc">
            <button onClick={() => setTocOpen(!tocOpen)} className="flex w-full items-center justify-between lg:cursor-default" data-testid="legal-toc-toggle">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-navy"><ListTree className="h-4 w-4 text-saffron" /> On This Page</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform lg:hidden ${tocOpen ? "rotate-180" : ""}`} />
            </button>
            <nav className={`mt-3 space-y-1 ${tocOpen ? "" : "hidden"} lg:block`}>
              {page.sections.map((s, i) => (
                <a key={i} href={`#${slugify(s.heading)}`} data-testid={`legal-toc-${i}`}
                   className="block rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-saffron/10 hover:text-saffron">
                  {s.heading}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* CONTENT */}
        <main className="lg:col-span-3" data-testid="legal-content">
          {slug === "privacy" && <PrivacyFlow />}
          {slug === "refund" && <RefundTimeline />}
          {page.sections.map((s, i) => (
            <section key={i} id={slugify(s.heading)} className="mb-8 scroll-mt-32 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid={`legal-section-${i}`}>
              <h2 className="flex items-center gap-2.5 font-heading text-lg font-extrabold text-navy sm:text-xl">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-saffron/10 text-xs font-extrabold text-saffron">{i + 1}</span>
                {s.heading}
              </h2>
              {slug === "shipping" && slugify(s.heading) === "packaging-requirements" && <PackingVisual />}
              {slug === "shipping" && slugify(s.heading) === "tracking" && <TrackingTimeline />}
              {s.body && <p className="mt-3.5 text-sm leading-relaxed text-slate-600">{s.body}</p>}
              {!!(s.bullets || []).length && (
                <ul className="mt-3.5 space-y-2">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex gap-2.5 text-sm leading-relaxed text-slate-600">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{b}
                    </li>
                  ))}
                </ul>
              )}
              {s.notice && (
                <p className="mt-4 flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />{s.notice}
                </p>
              )}
              {s.link_href && (
                <Link to={s.link_href} data-testid={`legal-link-${i}`}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-express">
                  {s.link_label || "Learn more"} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </section>
          ))}

          {/* Cross links */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="legal-crosslinks">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Related</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {meta.cross.map(([l, to]) => (
                <Link key={to + l} to={to} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-navy transition-colors hover:border-saffron hover:text-saffron">
                  {l} <ArrowRight className="h-3 w-3" />
                </Link>
              ))}
            </div>
          </div>
        </main>
      </div>

      <NeedHelp contact={contact} />
      <Footer contact={contact} />
    </div>
  );
}
